import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  ConfirmDialog,
  ResultDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { adminWithdrawalsApi } from "@/services/api";
import {
  ArrowUpRight,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Search,
  Building2,
  Banknote,
  ExternalLink,
  X,
  MoreHorizontal,
  Eye,
  Ban,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface Withdrawal {
  id: string;
  companyName: string;
  amount: number;
  amountFormatted: string;
  principalAmount?: number;
  yieldAmount?: number;
  status: string;
  bankAccount: string;
  requestedAt: string;
  createdAt: string;
}

interface Stats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  cancelled: number;
  totalAmountPending: number;
  totalAmountCompleted: number;
  bankBalance?: number;
}

interface WithdrawalDetail {
  id: string;
  companyId: string;
  companyName: string;
  companyCnpj: string;
  amount: number;
  amountFormatted: string;
  principalAmount?: number;
  principalAmountFormatted?: string;
  yieldAmount?: number;
  yieldAmountFormatted?: string;
  withdrawalType: string;
  status: string;
  bankAccount: {
    bankName: string;
    agency: string;
    accountNumber: string;
    accountDigit?: string;
    holderName: string;
    pixKey?: string;
  };
  requestedAt: string;
  requestedByUser?: string;
  processingStartedAt?: string;
  processingCompletedAt?: string;
  paidAt?: string;
  burnBrldTxHash?: string;
  mintBrldyTxHash?: string;
  burnBrldyTxHash?: string;
  bankTransferTxId?: string;
  walletAddress?: string;
  errorMessage?: string;
  cancellationReason?: string;
  createdAt: string;
}

const STATUS_CONFIG: Record<string, {
  label: string;
  variant: "secondary" | "warning" | "success" | "destructive";
  icon: typeof Clock;
  description: string;
}> = {
  PENDING: {
    label: "Pendente",
    variant: "secondary",
    icon: Clock,
    description: "Aguardando processamento",
  },
  PROCESSING: {
    label: "Processando",
    variant: "warning",
    icon: Loader2,
    description: "Transferência em andamento",
  },
  COMPLETED: {
    label: "Concluído",
    variant: "success",
    icon: CheckCircle,
    description: "Resgate finalizado",
  },
  FAILED: {
    label: "Falhou",
    variant: "destructive",
    icon: XCircle,
    description: "Erro no processamento",
  },
  CANCELLED: {
    label: "Cancelado",
    variant: "secondary",
    icon: X,
    description: "Cancelado pelo usuário",
  },
};

const WITHDRAWAL_TYPE_LABELS: Record<string, string> = {
  ALL: "Tudo",
  PRINCIPAL_ONLY: "Apenas Principal",
  YIELD_ONLY: "Apenas Rendimento",
  CUSTOM: "Valor Personalizado",
};

export function WithdrawalsPage() {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [companySearch, setCompanySearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
    loading: false,
  });

  // Result dialog
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "success",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, [statusFilter, companySearch, page]);

  async function loadData() {
    setLoading(true);
    try {
      const [withdrawalsData, statsData] = await Promise.all([
        adminWithdrawalsApi.list({
          status: statusFilter || undefined,
          companySearch: companySearch || undefined,
          page,
          limit: 20,
        }),
        adminWithdrawalsApi.getStats(),
      ]);
      setWithdrawals(withdrawalsData.data);
      setTotal(withdrawalsData.total);
      setTotalPages(withdrawalsData.totalPages);
      setStats(statsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    setDetailLoading(true);
    try {
      const detail = await adminWithdrawalsApi.getById(id);
      setSelectedWithdrawal(detail);
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleRetry(id: string) {
    setConfirmDialog({
      open: true,
      title: "Reprocessar Resgate",
      description: "Deseja reprocessar este resgate? O sistema tentará executar a transferência novamente.",
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await adminWithdrawalsApi.retry(id);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: result.success ? "success" : "error",
            title: result.success ? "Resgate reprocessado" : "Erro ao reprocessar",
            description: result.message,
          });
          await loadData();
          if (selectedWithdrawal?.id === id) {
            await loadDetail(id);
          }
        } catch (error) {
          console.error("Erro ao reprocessar:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao reprocessar",
            description: "Não foi possível reprocessar o resgate. Tente novamente.",
          });
        }
      },
    });
  }

  async function handleCancel(id: string) {
    setConfirmDialog({
      open: true,
      title: "Recusar Resgate",
      description: "Deseja recusar este resgate? O saldo bloqueado será liberado para a empresa.",
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await adminWithdrawalsApi.cancel(id, "Recusado pelo administrador");
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: result.success ? "success" : "error",
            title: result.success ? "Resgate recusado" : "Erro ao recusar",
            description: result.message,
          });
          await loadData();
          if (selectedWithdrawal?.id === id) {
            setSelectedWithdrawal(null);
          }
        } catch (error) {
          console.error("Erro ao recusar:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao recusar",
            description: "Não foi possível recusar o resgate. Tente novamente.",
          });
        }
      },
    });
  }

  function handleSearch() {
    setPage(1);
    setCompanySearch(searchInput);
  }

  function getStatusBadge(status: string) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === "PROCESSING" ? "animate-spin" : ""}`} />
        {config.label}
      </Badge>
    );
  }

  function formatDocument(doc: string) {
    if (!doc) return "";
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    if (doc.length === 14) {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return doc;
  }

  if (loading && withdrawals.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<ArrowUpRight className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Resgates"
          description="Gerenciamento de solicitações de resgate"
        />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ArrowUpRight className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Resgates"
        description="Gerenciamento de solicitações de resgate"
        stats={
          stats
            ? [
                { value: stats.total, label: "Total" },
                { value: stats.completed, label: "Concluídos" },
                { value: stats.pending + stats.processing, label: "Pendentes" },
                { value: formatCurrency(stats.totalAmountCompleted / 100), label: "Volume" },
              ]
            : []
        }
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter(""); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold">{stats?.total || 0}</p>
            <p className="text-sm text-muted-foreground">Todos</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "PENDING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("PENDING"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-yellow-500">{stats?.pending || 0}</p>
            <p className="text-sm text-muted-foreground">Pendentes</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "PROCESSING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("PROCESSING"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-blue-500">{stats?.processing || 0}</p>
            <p className="text-sm text-muted-foreground">Processando</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "COMPLETED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("COMPLETED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-green-500">{stats?.completed || 0}</p>
            <p className="text-sm text-muted-foreground">Concluídos</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "FAILED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("FAILED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-red-500">{stats?.failed || 0}</p>
            <p className="text-sm text-muted-foreground">Falhas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "CANCELLED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => { setStatusFilter("CANCELLED"); setPage(1); }}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-gray-500">{stats?.cancelled || 0}</p>
            <p className="text-sm text-muted-foreground">Cancelados</p>
          </CardContent>
        </Card>
      </div>

      {/* Bank Balance Info */}
      {stats?.bankBalance !== undefined && stats?.totalAmountPending > 0 && (
        <Card className={stats.bankBalance < stats.totalAmountPending ? "border-warning bg-warning/10" : ""}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Banknote className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Saldo Disponível no Itaú</p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(stats.bankBalance / 100)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Pendente</p>
                <p className="font-medium text-yellow-500">
                  {formatCurrency(stats.totalAmountPending / 100)}
                </p>
              </div>
              {stats.bankBalance < stats.totalAmountPending && (
                <div className="flex items-center gap-2 text-warning">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium">Saldo insuficiente</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por empresa (nome ou CNPJ)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button onClick={handleSearch}>Buscar</Button>
            {companySearch && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                  setCompanySearch("");
                  setPage(1);
                }}
              >
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Withdrawals Table */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Resgates {statusFilter && `(${STATUS_CONFIG[statusFilter]?.label})`}
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {withdrawals.length} de {total}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {withdrawals.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Banknote className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum resgate encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-visible">
                <table className="w-full">
                  <thead className="border-b border-border bg-muted/30">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="p-4 font-medium">Data</th>
                      <th className="p-4 font-medium">Empresa</th>
                      <th className="p-4 font-medium">Valor</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((withdrawal) => (
                      <tr
                        key={withdrawal.id}
                        className={`border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer ${
                          selectedWithdrawal?.id === withdrawal.id ? "bg-muted/30" : ""
                        }`}
                        onClick={() => loadDetail(withdrawal.id)}
                      >
                        <td className="p-4">
                          <p className="font-medium">{formatDate(withdrawal.requestedAt)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(withdrawal.requestedAt).toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </td>
                        <td className="p-4">
                          <div className="flex items-start gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium">{withdrawal.companyName}</p>
                              <p className="text-xs text-muted-foreground">
                                {withdrawal.bankAccount}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-semibold text-red-500">
                            - {withdrawal.amountFormatted}
                          </p>
                          {(withdrawal.principalAmount || withdrawal.yieldAmount) && (
                            <div className="text-xs text-muted-foreground">
                              {withdrawal.principalAmount && (
                                <span>P: {formatCurrency(withdrawal.principalAmount / 100)}</span>
                              )}
                              {withdrawal.principalAmount && withdrawal.yieldAmount && " | "}
                              {withdrawal.yieldAmount && (
                                <span className="text-green-600">
                                  Y: {formatCurrency(withdrawal.yieldAmount / 100)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4">{getStatusBadge(withdrawal.status)}</td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => loadDetail(withdrawal.id)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver detalhes
                              </DropdownMenuItem>
                              {withdrawal.status === "PENDING" && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel(withdrawal.id);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Recusar
                                </DropdownMenuItem>
                              )}
                              {withdrawal.status === "FAILED" && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetry(withdrawal.id);
                                  }}
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Reprocessar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalhes do Resgate</CardTitle>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : selectedWithdrawal ? (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  {getStatusBadge(selectedWithdrawal.status)}
                  <span className="text-xs text-muted-foreground">
                    {selectedWithdrawal.id.slice(0, 8)}...
                  </span>
                </div>

                {/* Valor */}
                <div className="bg-muted rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-2xl font-bold text-red-500">
                    - {selectedWithdrawal.amountFormatted}
                  </p>
                </div>

                {/* Split Principal/Yield */}
                {(selectedWithdrawal.principalAmount || selectedWithdrawal.yieldAmount) && (
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Principal</span>
                      <span>{selectedWithdrawal.principalAmountFormatted || "R$ 0,00"}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Rendimento</span>
                      <span className="text-green-600">
                        {selectedWithdrawal.yieldAmountFormatted || "R$ 0,00"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-border">
                      <span className="text-muted-foreground">Tipo</span>
                      <span>{WITHDRAWAL_TYPE_LABELS[selectedWithdrawal.withdrawalType] || selectedWithdrawal.withdrawalType}</span>
                    </div>
                  </div>
                )}

                {/* Empresa */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Empresa</p>
                  <p className="font-medium">{selectedWithdrawal.companyName}</p>
                  <p className="text-xs text-muted-foreground">
                    CNPJ: {formatDocument(selectedWithdrawal.companyCnpj)}
                  </p>
                </div>

                {/* Conta Destino */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Conta de Destino</p>
                  <p className="font-medium">{selectedWithdrawal.bankAccount.bankName}</p>
                  <p className="text-xs text-muted-foreground">
                    Ag. {selectedWithdrawal.bankAccount.agency} | CC{" "}
                    {selectedWithdrawal.bankAccount.accountNumber}
                    {selectedWithdrawal.bankAccount.accountDigit && `-${selectedWithdrawal.bankAccount.accountDigit}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedWithdrawal.bankAccount.holderName}
                  </p>
                  {selectedWithdrawal.bankAccount.pixKey && (
                    <p className="text-xs text-muted-foreground">
                      PIX: {selectedWithdrawal.bankAccount.pixKey}
                    </p>
                  )}
                </div>

                {/* Timeline */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Timeline</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Solicitado</span>
                      <span>{formatDate(selectedWithdrawal.requestedAt)}</span>
                    </div>
                    {selectedWithdrawal.requestedByUser && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Por</span>
                        <span>{selectedWithdrawal.requestedByUser}</span>
                      </div>
                    )}
                    {selectedWithdrawal.processingStartedAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processamento</span>
                        <span>{formatDate(selectedWithdrawal.processingStartedAt)}</span>
                      </div>
                    )}
                    {selectedWithdrawal.paidAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pago em</span>
                        <span className="text-green-600">{formatDate(selectedWithdrawal.paidAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Blockchain Transactions */}
                {(selectedWithdrawal.burnBrldTxHash || selectedWithdrawal.mintBrldyTxHash || selectedWithdrawal.burnBrldyTxHash) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Transações Blockchain</p>
                    <div className="space-y-1">
                      {selectedWithdrawal.burnBrldTxHash && (
                        <a
                          href={`https://explorer.xinfin.network/tx/${selectedWithdrawal.burnBrldTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <span>Burn BRLD: {selectedWithdrawal.burnBrldTxHash.slice(0, 12)}...</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {selectedWithdrawal.mintBrldyTxHash && (
                        <a
                          href={`https://explorer.xinfin.network/tx/${selectedWithdrawal.mintBrldyTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <span>Mint BRLDY: {selectedWithdrawal.mintBrldyTxHash.slice(0, 12)}...</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {selectedWithdrawal.burnBrldyTxHash && (
                        <a
                          href={`https://explorer.xinfin.network/tx/${selectedWithdrawal.burnBrldyTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <span>Burn BRLDY: {selectedWithdrawal.burnBrldyTxHash.slice(0, 12)}...</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Bank Transfer */}
                {selectedWithdrawal.bankTransferTxId && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Transferência Bancária</p>
                    <p className="text-xs font-mono">{selectedWithdrawal.bankTransferTxId}</p>
                  </div>
                )}

                {/* Error Message */}
                {selectedWithdrawal.errorMessage && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                    <p className="text-sm font-medium text-destructive mb-1">Erro</p>
                    <p className="text-xs text-destructive">{selectedWithdrawal.errorMessage}</p>
                  </div>
                )}

                {/* Cancellation Reason */}
                {selectedWithdrawal.cancellationReason && (
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Motivo do Cancelamento</p>
                    <p className="text-xs">{selectedWithdrawal.cancellationReason}</p>
                  </div>
                )}

                {/* Actions */}
                {selectedWithdrawal.status === "FAILED" && (
                  <Button
                    className="w-full"
                    onClick={() => handleRetry(selectedWithdrawal.id)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reprocessar Resgate
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">Selecione um resgate para ver os detalhes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Reprocessar"
        variant="default"
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
